/**
 * RejectDialog component.
 *
 * Opens when the operator clicks [REJECT] or presses `r` on a card they own.
 *
 * Provides:
 * - Quick reasons (tool-specific + default) as checkboxes
 * - Freeform feedback textarea
 * - Validation: at least one reason OR non-empty feedback required
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Mic, ChevronRight } from 'lucide-react'
import {
  Button,
  Checkbox,
  Kbd,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui'
import type { QuickRejectReason, SourceContext } from '../../types'
import {
  CardTranslationProvider,
  CardTranslateControl,
  useCardTranslatedText,
} from '../approval/CardTranslation'
import {
  useSpeechRecognition,
  type SpeechRecognitionStatus,
} from '@/features/session/hooks/useSpeechRecognition'
import { useToast } from '@/shared/hooks/use-toast'

export interface RejectDialogProps {
  /** Whether the dialog is open. */
  open: boolean
  /** Called when the dialog should close. */
  onOpenChange: (open: boolean) => void
  /** Tool-specific quick reasons from the card. */
  quickRejectReasons: QuickRejectReason[]
  /** Default reasons from the registry. */
  defaultQuickRejectReasons?: QuickRejectReason[]
  /** Called when the user submits the rejection. */
  onSubmit: (data: { quickReasons: string[]; feedback: string }) => void
  /** Whether submission is pending. */
  isPending?: boolean
  /** Error message to display inline. */
  error?: string | null
  /** Agent ID for the toast message. */
  agentId?: string
  /**
   * Override for the dialog title.
   *
   * Defaults to "Reject Approval". Communication-style tools (e.g.
   * escalate_to_operator) set this to "Respond to Agent" so the operator
   * understands they are sending guidance, not rejecting an action.
   */
  dialogTitle?: string
  /**
   * Override for the dialog description / subtitle.
   *
   * Defaults to the standard "select reasons…" prompt.
   */
  dialogDescription?: string
  /**
   * Override for the submit button label.
   *
   * Defaults to "Reject". Communication-style tools set this to
   * "Send Response".
   */
  submitLabel?: string
  /**
   * When true, the Quick Reasons sections are hidden.
   *
   * Set for communication-style tools (e.g. escalate_to_operator) where
   * the dialog is a free-form response composer, not a rejection picker.
   */
  hideQuickReasons?: boolean
  /**
   * Variant for the submit button.
   *
   * Defaults to "destructive" (red) for standard rejection. Set to
   * "default" (green) for communication-style tools where the action
   * is sending a response, not rejecting.
   */
  submitVariant?: 'destructive' | 'default'
  /**
   * The original item being responded to (the review text, or the inbound
   * email being replied to), surfaced read-only above the feedback area so the
   * operator never has to leave the modal to see what they are acting on
   * (Task 4074b). Optional — when absent the dialog is a plain feedback
   * composer, unchanged. Built by `buildSourceContext` from the card sections.
   */
  sourceContext?: SourceContext
  /**
   * The response the agent proposed (and is asking the operator to approve),
   * surfaced read-only alongside the original item so the operator can see what
   * is being rejected while they type their feedback. Optional — when absent the
   * block is omitted. Built by `buildProposedResponse` from the card sections.
   */
  proposedResponse?: SourceContext
}

/** Default reasons shown when no registry defaults are provided. */
const FALLBACK_DEFAULT_REASONS: QuickRejectReason[] = [
  {
    id: 'wrong_timing',
    label: 'Wrong timing',
    feedbackText: 'Wrong timing - please reconsider when to take this action.',
  },
  {
    id: 'needs_investigation',
    label: 'Needs more investigation',
    feedbackText: 'More investigation needed before proceeding with this action.',
  },
  {
    id: 'factually_incorrect',
    label: 'Factually incorrect',
    feedbackText:
      'The information in this proposed action appears to be factually incorrect - please verify before proceeding.',
  },
  {
    id: 'policy_violation',
    label: 'Policy violation',
    feedbackText:
      'This action appears to violate policy - please review the relevant policies before proceeding.',
  },
  {
    id: 'check_instructions',
    label: 'Check your instructions',
    feedbackText:
      'Check your instructions, there may be updates to your system instruction, memories or other context that have been added since you decided on this action.',
  },
]

/**
 * Returns the text to insert into the rejection message textarea for a given reason.
 * Uses `feedbackText` if provided, otherwise falls back to `label`.
 */
function getReasonFeedbackText(reason: QuickRejectReason): string {
  return reason.feedbackText ?? reason.label
}

/**
 * Removes a previously-contributed text block from the overall message string.
 * Strips surrounding newlines so the textarea stays clean.
 */
function removeContributedText(message: string, contributed: string): string {
  // Try removing with a preceding newline first
  let result = message.replace('\n' + contributed, '')
  if (result !== message) return result.trimStart()
  // Try removing with a trailing newline
  result = message.replace(contributed + '\n', '')
  if (result !== message) return result.trimStart()
  // Plain replacement as last resort — trimStart cleans up any leftover leading space
  return message.replace(contributed, '').trimStart()
}

export function RejectDialog({
  open,
  onOpenChange,
  quickRejectReasons,
  defaultQuickRejectReasons,
  onSubmit,
  isPending = false,
  error = null,
  dialogTitle = 'Reject Approval',
  dialogDescription = 'Select reasons for rejection and optionally add feedback.',
  submitLabel = 'Reject',
  hideQuickReasons = false,
  submitVariant = 'destructive',
  sourceContext,
  proposedResponse,
}: RejectDialogProps) {
  const { toast } = useToast()

  // When source context is shown, the quick-reason groups are collapsed by
  // default so the source text is the primary content. Reset on (re)open.
  const [reasonsExpanded, setReasonsExpanded] = React.useState(false)

  // Selected quick reason IDs
  const [selectedReasons, setSelectedReasons] = React.useState<Set<string>>(new Set())
  // Freeform feedback text (also receives quick-reason snippets when toggled)
  const [feedback, setFeedback] = React.useState('')
  // Tracks the exact text each selected reason contributed so we can remove it on uncheck
  const [reasonTextContributions, setReasonTextContributions] = React.useState<
    Map<string, string>
  >(new Map())

  // ── Dictation ──────────────────────────────────────────────────────────
  const feedbackRef = React.useRef(feedback)
  React.useEffect(() => { feedbackRef.current = feedback }, [feedback])
  const [micStatus, setMicStatus] = React.useState<SpeechRecognitionStatus>('inactive')
  const [interimTranscript, setInterimTranscript] = React.useState('')

  const handleMicTranscript = React.useCallback((transcript: string, isFinal: boolean) => {
    if (isFinal) {
      const trimmed = transcript.trim()
      if (!trimmed) return
      const prev = feedbackRef.current
      const needsSpace = prev.length > 0 && !/\s$/.test(prev)
      setFeedback(prev + (needsSpace ? ' ' : '') + trimmed)
      setInterimTranscript('')
    } else {
      setInterimTranscript(transcript)
    }
  }, [])

  const handleMicError = React.useCallback(
    (message: string) => {
      toast({ title: 'Dictation', description: message, variant: 'danger' })
      setInterimTranscript('')
    },
    [toast],
  )

  const {
    start: startDictation,
    stop: stopDictation,
    isSupported: isMicSupported,
  } = useSpeechRecognition({
    onTranscript: handleMicTranscript,
    onStatusChange: setMicStatus,
    onError: handleMicError,
  })

  const isListening = micStatus === 'listening'

  const toggleDictation = React.useCallback(() => {
    if (isListening) {
      stopDictation()
      setInterimTranscript('')
    } else {
      startDictation()
    }
  }, [isListening, startDictation, stopDictation])

  // Reset state when dialog opens; stop dictation when it closes
  React.useEffect(() => {
    if (open) {
      setSelectedReasons(new Set())
      setFeedback('')
      setReasonTextContributions(new Map())
      setInterimTranscript('')
      setReasonsExpanded(false)
    } else {
      stopDictation()
      setInterimTranscript('')
    }
  }, [open, stopDictation])

  // Merge default reasons (use registry defaults or fallback)
  const effectiveDefaults =
    defaultQuickRejectReasons && defaultQuickRejectReasons.length > 0
      ? defaultQuickRejectReasons
      : FALLBACK_DEFAULT_REASONS

  // Validation: at least one reason OR non-empty feedback
  const isValid = selectedReasons.size > 0 || feedback.trim().length > 0

  const handleToggleReason = React.useCallback(
    (id: string, checked: boolean, feedbackText: string) => {
      setSelectedReasons((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(id)
        } else {
          next.delete(id)
        }
        return next
      })

      if (checked) {
        // Append the reason's text to the textarea
        setFeedback((prev) => {
          const trimmed = prev.trimEnd()
          return trimmed.length > 0 ? trimmed + '\n' + feedbackText : feedbackText
        })
        setReasonTextContributions((prev) => new Map(prev).set(id, feedbackText))
      } else {
        // Remove the contributed text from the textarea
        const contributed = reasonTextContributions.get(id) ?? feedbackText
        setFeedback((prev) => removeContributedText(prev, contributed))
        setReasonTextContributions((prev) => {
          const next = new Map(prev)
          next.delete(id)
          return next
        })
      }
    },
    [reasonTextContributions],
  )

  const handleSubmit = React.useCallback(() => {
    if (!isValid || isPending) return
    onSubmit({
      quickReasons: Array.from(selectedReasons),
      feedback: feedback.trim(),
    })
  }, [isValid, isPending, selectedReasons, feedback, onSubmit])

  // Handle Cmd/Ctrl+Enter keyboard shortcut
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  // The reason groups (tool-specific + defaults) shown to the operator. When a
  // source context is present they are collapsed by default behind a toggle so
  // the source text leads; otherwise they render inline as before.
  // Renders the reason checkbox groups. `showHeadings` is false when the groups
  // live inside the collapsible (whose toggle already labels the section), so we
  // don't duplicate the "Quick Reasons" heading. Checkboxes lay out in a
  // responsive multi-column grid so an expanded list stays compact rather than
  // pushing the message box and footer off-screen.
  const renderReasonGroups = (showHeadings: boolean) =>
    !hideQuickReasons && (
      <>
        {/* Tool-specific quick reasons — hidden for communication-style tools */}
        {quickRejectReasons.length > 0 && (
          <div style={styles.reasonSection}>
            {showHeadings && (
              <h3 style={styles.reasonSectionHeading}>Quick Reasons</h3>
            )}
            <div style={styles.reasonList}>
              {quickRejectReasons.map((reason) => (
                <QuickReasonCheckbox
                  key={reason.id}
                  reason={reason}
                  checked={selectedReasons.has(reason.id)}
                  onCheckedChange={(checked) =>
                    handleToggleReason(reason.id, checked, getReasonFeedbackText(reason))
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Default reasons — hidden for communication-style tools */}
        <div style={styles.reasonSection}>
          {showHeadings && (
            <h3 style={styles.reasonSectionHeading}>
              {quickRejectReasons.length > 0 ? 'Default Reasons' : 'Quick Reasons'}
            </h3>
          )}
          <div style={styles.reasonList}>
            {effectiveDefaults.map((reason) => (
              <QuickReasonCheckbox
                key={reason.id}
                reason={reason}
                checked={selectedReasons.has(reason.id)}
                onCheckedChange={(checked) =>
                  handleToggleReason(reason.id, checked, getReasonFeedbackText(reason))
                }
              />
            ))}
          </div>
        </div>
      </>
    )

  const hasReasonGroups = !hideQuickReasons

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={styles.dialogContent}
        onKeyDown={handleKeyDown}
        aria-describedby={error ? 'reject-error' : 'reject-helper'}
      >
        {/* The dialog portals OUT of the card's CardTranslationProvider, so it
            owns its own provider here. This powers the in-modal translate
            toggle on the source block (Task 4074b, Decision 3). */}
        <CardTranslationProvider>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription style={styles.srOnly}>
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>

        <div style={styles.content}>
          {/* Context row — the original review / inbound email being responded
              to (left) and the agent's proposed response being rejected (right),
              shown read-only directly above the feedback area so the operator
              never has to leave the modal to see what they are acting on. With
              the wider modal these sit side-by-side; each falls back to full
              width when it's the only block present. Includes the shared
              card-level translate control. */}
          {(sourceContext || proposedResponse) && (
            <div style={styles.contextRow}>
              {sourceContext && <SourceContextBlock sourceContext={sourceContext} />}
              {proposedResponse && (
                <SourceContextBlock sourceContext={proposedResponse} accent />
              )}
            </div>
          )}

          {/* Quick reasons. When a source context is shown they collapse behind
              a toggle (collapsed by default) so the source leads; otherwise
              they render inline. Always hidden for communication-style tools. */}
          {hasReasonGroups && (
            (sourceContext || proposedResponse) ? (
              <div style={styles.reasonsCollapsible}>
                <button
                  type="button"
                  onClick={() => setReasonsExpanded((v) => !v)}
                  aria-expanded={reasonsExpanded}
                  style={styles.reasonsToggle}
                  className="link-hover"
                >
                  <ChevronRight
                    style={{
                      ...styles.reasonsToggleChevron,
                      transform: reasonsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                    aria-hidden="true"
                  />
                  Quick reasons
                </button>
                {reasonsExpanded && (
                  <div style={styles.reasonsCollapsibleBody}>
                    {/* Headings suppressed — the toggle above is the section label */}
                    {renderReasonGroups(false)}
                  </div>
                )}
              </div>
            ) : (
              renderReasonGroups(true)
            )
          )}

          {/* Message textarea — quick reasons populate this automatically.
              Microphone button sits bottom-right for voice dictation. */}
          <div style={styles.feedbackSection}>
            <label
              htmlFor="reject-feedback"
              style={styles.feedbackLabel}
            >
              Message to agent{' '}
              <span style={styles.optionalText}>(sent as text on rejection)</span>
            </label>
            <div style={styles.textareaWrapper}>
              <textarea
                id="reject-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={
                  isListening
                    ? 'Listening...'
                    : hideQuickReasons
                    ? 'Write your response to the agent...'
                    : 'Select a quick reason above, or write your own message...'
                }
                rows={4}
                aria-describedby="reject-helper"
                style={styles.textarea}
                className="textarea-focus"
              />
              {/* Interim transcript ghost — shown while dictating */}
              {interimTranscript && (
                <span style={styles.interimTranscript} aria-live="polite">
                  {feedback && !/\s$/.test(feedback) ? ' ' : ''}
                  {interimTranscript}
                </span>
              )}
              {/* Microphone button — hidden when Web Speech API is unavailable.
                  Kept intentionally subtle (low opacity, small icon) so it
                  doesn't compete with the textarea content. */}
              {isMicSupported && (
                <button
                  type="button"
                  onClick={toggleDictation}
                  aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
                  aria-pressed={isListening}
                  style={{
                    ...styles.micButton,
                    color: isListening ? 'var(--error)' : 'var(--text-faint)',
                    opacity: isListening ? 1 : 0.4,
                  }}
                >
                  <Mic style={{ width: '12px', height: '12px' }} />
                  {isListening && (
                    <span
                      style={styles.micPulseDot}
                      className="animate-pulse"
                      aria-hidden="true"
                    />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Helper text / validation message */}
          {!isValid && (
            <p
              id="reject-helper"
              style={styles.helperText}
            >
              {hideQuickReasons
                ? 'Write your response to the agent.'
                : 'Select at least one reason or write feedback.'}
            </p>
          )}

          {/* Error message */}
          {error && (
            <p
              id="reject-error"
              style={styles.errorText}
              role="alert"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter style={styles.dialogFooter}>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
            <Kbd style={styles.kbdInline}>Esc</Kbd>
          </Button>
          <Button
            variant={submitVariant}
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            style={submitVariant === 'default' ? { background: 'var(--success)', color: 'white' } : undefined}
          >
            {isPending ? 'Submitting...' : submitLabel}
            {!isPending && <Kbd style={styles.kbdInline} className="kbd-hidden-mobile">Cmd+Enter</Kbd>}
          </Button>
        </DialogFooter>
        </CardTranslationProvider>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Read-only block showing the original item (review / inbound email) the
 * operator is responding to. Rendered above the feedback area. The body flows
 * through `useCardTranslatedText` so the in-modal `CardTranslateControl` flips
 * it between the original language and English; the control renders nothing
 * unless the body (or another registered field) looks translatable.
 */
function SourceContextBlock({
  sourceContext,
  accent = false,
}: {
  sourceContext: SourceContext
  /** When true, styles the block as the agent's proposed response (accent border). */
  accent?: boolean
}) {
  const shownBody = useCardTranslatedText(sourceContext.body)

  return (
    <section
      style={{
        ...styles.sourceBlock,
        ...(accent ? styles.sourceBlockAccent : null),
      }}
      aria-label={sourceContext.label}
    >
      <div style={styles.sourceHeader}>
        <h3 style={styles.reasonSectionHeading}>{sourceContext.label}</h3>
        <CardTranslateControl />
      </div>
      {sourceContext.fields && sourceContext.fields.length > 0 && (
        <dl style={styles.sourceFields}>
          {sourceContext.fields.map((field) => (
            <div key={field.label} style={styles.sourceFieldRow}>
              <dt style={styles.sourceFieldKey}>{field.label}</dt>
              <dd style={styles.sourceFieldValue}>{field.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <blockquote style={styles.sourceBody}>{shownBody}</blockquote>
    </section>
  )
}

/**
 * Quick reason checkbox.
 */
interface QuickReasonCheckboxProps {
  reason: QuickRejectReason
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

function QuickReasonCheckbox({
  reason,
  checked,
  onCheckedChange,
}: QuickReasonCheckboxProps) {
  return (
    <div style={styles.checkboxRow}>
      <Checkbox
        id={`reason-${reason.id}`}
        checked={checked}
        onCheckedChange={onCheckedChange}
        label={reason.label}
      />
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  dialogContent: {
    // Doubled from 512px so long reviews / emails wrap more naturally and the
    // original item and proposed response can sit side-by-side. Capped at the
    // viewport so it stays usable on smaller windows.
    maxWidth: 'min(1024px, 92vw)',
    width: 'min(1024px, 92vw)',
  },
  dialogFooter: {
    display: 'flex',
    gap: '8px',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  // ── Source-context block (Task 4074b) ─────────────────────────────────────
  // Row holding the original item and the proposed response side-by-side. Each
  // child has flex:1 so a lone block still fills the full width.
  contextRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'stretch',
  },
  sourceBlock: {
    flex: '1 1 360px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '12px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-tertiary)',
  },
  // Proposed response accent — distinguishes the agent's draft from the original.
  sourceBlockAccent: {
    border: '1px solid var(--border-default)',
    borderLeft: '3px solid var(--accent, var(--text-muted))',
  },
  sourceHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  sourceFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    margin: 0,
  },
  sourceFieldRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    minWidth: 0,
  },
  sourceFieldKey: {
    flexShrink: 0,
    width: '64px',
    fontSize: '12px',
    color: 'var(--text-muted)',
    margin: 0,
  },
  sourceFieldValue: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
    minWidth: 0,
    margin: 0,
  },
  sourceBody: {
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    // Long reviews / emails scroll within the block instead of pushing the
    // feedback area off-screen (polish — Step 6).
    maxHeight: '32vh',
    overflowY: 'auto',
  },
  // ── Collapsible quick reasons (shown when a source context is present) ─────
  reasonsCollapsible: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  reasonsToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    padding: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted)',
  },
  reasonsToggleChevron: {
    width: '14px',
    height: '14px',
    transition: 'transform var(--transition-fast)',
  },
  reasonsCollapsibleBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  reasonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  reasonSectionHeading: {
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted)',
  },
  reasonList: {
    // Responsive multi-column grid so an expanded list of reasons stays compact
    // and doesn't push the message box / footer off-screen on the wide modal.
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    columnGap: '24px',
    rowGap: '8px',
  },
  feedbackSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  feedbackLabel: {
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted)',
  },
  optionalText: {
    textTransform: 'none',
  },
  textareaWrapper: {
    position: 'relative',
  },
  textarea: {
    width: '100%',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    padding: '8px 28px 8px 12px', // right padding leaves minimal room for the small mic button
    fontSize: '14px',
    color: 'var(--text-primary)',
    resize: 'none',
    boxSizing: 'border-box',
  },
  interimTranscript: {
    position: 'absolute',
    left: '12px',
    right: '28px',
    top: '8px',
    bottom: 0,
    pointerEvents: 'none',
    color: 'var(--text-faint)',
    fontSize: '14px',
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontStyle: 'italic',
  },
  micButton: {
    position: 'absolute',
    bottom: '7px',
    right: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    transition: 'color var(--transition-fast), opacity var(--transition-fast)',
  },
  micPulseDot: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: 'var(--error)',
    boxShadow: '0 0 3px var(--error)',
  },
  helperText: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  errorText: {
    fontSize: '14px',
    color: 'var(--error)',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  kbdInline: {
    marginLeft: '8px',
  },
}

export default RejectDialog
